import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useState } from "react";
import Layout from '../../Layout'
import useAuthStore from "../../store/authStore";
import useTableColumnSettings from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";
import {
    Input,
    Table,
    Tag,
    Button,
    Popconfirm,
    message,
    Modal,
    Form,
    InputNumber,
    Switch,
    Select,
    Spin,
} from "antd";
import {
    SearchOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import {
    getServices,
    getServiceCategories,
    createService,
    updateService,
    deleteService,
    updateServicesDisplayArea,
    type ServiceItem,
    type ServiceCategoryItem,
} from "../../api/Service";

/* ================= FORMAT ================= */
const removeDiacritics = (str: string) =>
    str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D');

const formatPrice = (num: number | string) => {
    const n = typeof num === 'string' ? parseFloat(num) : num;
    return n.toLocaleString("vi-VN") + " đ";
};

/** Khu vực hiển thị duy nhất đang dùng: dịch vụ hiện ở màn đăng ký của kiosk Check-in */
const CHECKIN_AREA = 'checkin';

/* ================= COMPONENT ================= */
export default function ServiceListPage() {
    const { t } = useTranslation();
    // Phân quyền thao tác: chỉ có quyền xem thì không hiện nút thêm/sửa/xóa
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canCreate = isSuperAdmin || hasPermission("services.create");
    const canEdit = isSuperAdmin || hasPermission("services.edit");
    const canDelete = isSuperAdmin || hasPermission("services.delete");
    const STATUS_MAP: Record<number, { label: string; color: string }> = {
        0: { label: t('sv.status_active'), color: "green" },
        1: { label: t('sv.status_locked'), color: "red" },
    };

    /* ===== STATE ===== */
    const [data, setData] = useState<ServiceItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [keyword, setKeyword] = useState("");
    const [tab, setTab] = useState("all");
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });

    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState<ServiceItem | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const [categories, setCategories] = useState<ServiceCategoryItem[]>([]);
    const [sortField, setSortField] = useState<string | undefined>();
    const [sortOrder, setSortOrder] = useState<string | undefined>();
    /** Lọc theo khu vực hiển thị: '' tất cả, CHECKIN_AREA đang hiện ở Check-in, 'none' chưa gán */
    const [areaFilter, setAreaFilter] = useState<string>('');
    /** Dịch vụ đang chọn để gán / gỡ hàng loạt; giữ qua các trang */
    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [bulkAction, setBulkAction] = useState<'add' | 'remove' | null>(null);

    /* ===== FETCH CATEGORIES ===== */
    useEffect(() => {
        getServiceCategories({ take: 100 }).then(res => {
            if (res.status) setCategories(res.data?.items || []);
        });
    }, []);

    /* ===== FETCH ===== */
    const fetchData = useCallback(async (page = 1, search = keyword, statusFilter?: number, sort?: string) => {
        setLoading(true);
        try {
            const res = await getServices({
                page,
                take: pagination.pageSize,
                search: search || undefined,
                status: statusFilter,
                display_area: areaFilter || undefined,
                sort: sort || undefined,
            });
            if (res.status) {
                setData(res.data?.items || []);
                setPagination(prev => ({
                    ...prev,
                    current: res.data?.current_page || 1,
                    total: res.data?.total || 0,
                }));
            }
        } finally {
            setLoading(false);
        }
    }, [keyword, pagination.pageSize, areaFilter]);

    useEffect(() => {
        const statusFilter = tab === 'active' ? 0 : tab === 'locked' ? 1 : undefined;
        const sort = sortField && sortOrder ? `${sortField},${sortOrder}` : undefined;
        fetchData(1, keyword, statusFilter, sort);
    }, [tab, areaFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ===== SEARCH ===== */
    const handleSearch = (value: string) => {
        setKeyword(value);
        const statusFilter = tab === 'active' ? 0 : tab === 'locked' ? 1 : undefined;
        const sort = sortField && sortOrder ? `${sortField},${sortOrder}` : undefined;
        fetchData(1, value, statusFilter, sort);
    };

    /* ===== COUNT ===== */
    const countActive = data.filter((i) => i.status === 0).length;
    const countLocked = data.filter((i) => i.status === 1).length;

    /* ===== ACTIONS ===== */
    const openAdd = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({ status: true, priority: 0, duration: 0 });
        setOpen(true);
    };

    const openEdit = (record: ServiceItem) => {
        setEditing(record);
        form.setFieldsValue({
            code: record.code,
            code_2: record.code_2,
            name: record.name,
            duration: record.duration,
            category_id: record.category_id || undefined,
            price: parseFloat(record.price),
            discount_price: parseFloat(record.discount_price),
            priority: record.priority,
            status: record.status === 0,
            description: record.description || '',
        });
        setOpen(true);
    };

    const handleDelete = async (id: number) => {
        const res = await deleteService(id);
        if (res.status) {
            message.success(res.msg || t('sv.deleted'));
            const statusFilter = tab === 'active' ? 0 : tab === 'locked' ? 1 : undefined;
            fetchData(pagination.current, keyword, statusFilter);
        } else {
            message.error(res.msg || t('sv.delete_failed'));
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                code: values.code,
                code_2: values.code_2,
                name: values.name,
                duration: values.duration || 0,
                category_id: values.category_id || undefined,
                price: values.price,
                discount_price: values.discount_price || 0,
                priority: values.priority || 0,
                status: values.status ? 0 : 1,
                description: values.description || undefined,
            };

            setSubmitting(true);
            if (editing) {
                const res = await updateService(editing.id, payload);
                if (res.status) {
                    message.success(res.msg || t('sv.updated'));
                } else {
                    message.error(res.msg || t('sv.update_failed'));
                    return;
                }
            } else {
                const res = await createService(payload);
                if (res.status) {
                    message.success(res.msg || t('sv.added'));
                } else {
                    message.error(res.msg || t('sv.add_failed'));
                    return;
                }
            }

            setOpen(false);
            const statusFilter = tab === 'active' ? 0 : tab === 'locked' ? 1 : undefined;
            fetchData(pagination.current, keyword, statusFilter);
        } catch {
            // validation error
        } finally {
            setSubmitting(false);
        }
    };

    /* ===== KHU VỰC HIỂN THỊ (BULK) ===== */
    const handleBulkDisplayArea = async (action: 'add' | 'remove') => {
        if (!selectedIds.length || bulkAction) return;
        setBulkAction(action);
        try {
            const res = await updateServicesDisplayArea(selectedIds.map(Number), action, CHECKIN_AREA);
            if (!res.status) {
                message.error(res.msg || t('sv.area_failed'));
                return;
            }
            message.success(res.msg || t(action === 'add' ? 'sv.area_added' : 'sv.area_removed', { count: selectedIds.length }));
            setSelectedIds([]);
            const statusFilter = tab === 'active' ? 0 : tab === 'locked' ? 1 : undefined;
            const sort = sortField && sortOrder ? `${sortField},${sortOrder}` : undefined;
            fetchData(pagination.current, keyword, statusFilter, sort);
        } finally {
            setBulkAction(null);
        }
    };

    /* ===== TABLE CHANGE ===== */
    const handleTableChange = (pag: any, _filters: any, sorter: any) => {
        const statusFilter = tab === 'active' ? 0 : tab === 'locked' ? 1 : undefined;
        const field = sorter.field;
        const order = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined;
        setSortField(order ? field : undefined);
        setSortOrder(order);
        const sort = field && order ? `${field},${order}` : undefined;
        fetchData(pag.current, keyword, statusFilter, sort);
    };

    /* ===== COLUMNS ===== */
    const columns = [
        {
            key: "stt",
            title: t('sv.col_stt'),
            width: 60,
            render: (_: any, __: any, index: number) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: t('sv.col_code_name'),
            dataIndex: "name",
            sorter: true,
            render: (_: any, record: ServiceItem) => (
                <div>
                    <div className="font-medium">{record.name}</div>
                    <div className="text-xs text-blue-500">{record.code}</div>
                    {record.code_2 && <div className="text-xs text-gray-400">{record.code_2}</div>}
                </div>
            ),
        },
        {
            key: "category",
            title: t('sv.col_category'),
            render: (_: any, record: ServiceItem) => record.category?.name ? (
                <span className="bg-gray-100 px-2 py-1 rounded text-xs dark:bg-gray-700">{record.category.name}</span>
            ) : '-',
        },
        {
            title: t('sv.col_price'),
            dataIndex: "price",
            sorter: true,
            render: (_: any, record: ServiceItem) => (
                <div>
                    <div className="font-semibold">{formatPrice(record.price)}</div>
                    {parseFloat(record.discount_price) > 0 && (
                        <div className="text-xs text-red-500">{t('sv.discount')}: {formatPrice(record.discount_price)}</div>
                    )}
                </div>
            ),
        },
        {
            title: t('sv.col_duration'),
            dataIndex: "duration",
            sorter: true,
            render: (val: number) => val > 0 ? `${val} ${t('sv.minutes')}` : '-',
        },
        {
            title: t('sv.col_status'),
            dataIndex: "status",
            sorter: true,
            render: (val: number) => {
                const s = STATUS_MAP[val] || STATUS_MAP[0];
                return <Tag color={s.color}>{s.label}</Tag>;
            },
        },
        {
            key: "display_area",
            title: t('sv.col_checkin'),
            dataIndex: "display_area",
            render: (val?: string | null) => val === CHECKIN_AREA ? <Tag color="blue">{t('sv.area_checkin')}</Tag> : '-',
        },
        {
            key: "actions",
            title: t('sv.col_action'),
            fixed: "right" as const,
            lockVisible: true,
            render: (_: any, record: ServiceItem) => (
                <div className="flex items-center gap-3 text-[16px]">
                    {canEdit && (
                        <EditOutlined
                            onClick={() => openEdit(record)}
                            className="text-blue-500 cursor-pointer"
                        />
                    )}
                    {canDelete && (
                        <Popconfirm
                            title={t('sv.confirm_delete')}
                            onConfirm={() => handleDelete(record.id)}
                        >
                            <DeleteOutlined className="text-red-500 cursor-pointer" />
                        </Popconfirm>
                    )}
                </div>
            ),
        },
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings(
        "service_list",
        (canEdit || canDelete) ? columns : columns.filter((c: any) => c.key !== "actions")
    );

    return (
        <Layout>
            <Helmet>
                <title>{t('sv.title')} | Khothi</title>
            </Helmet>
            <div>
                <h2 className="text-xl font-bold text-gray-900 text-000000e0 mb-4">
                    {t('sv.title')}
                </h2>
            </div>

            {/* HEADER */}
            <div className="flex justify-between mb-4 gap-4 flex-col md:flex-row items-start md:items-center">
                {/* TABS */}
                <div className="flex gap-2">
                    <button
                        onClick={() => setTab("all")}
                        className={`text-[14px] px-3 py-1 rounded ${tab === "all" ? "bg-gray-800 text-white" : "bg-gray-100"}`}
                    >
                        {t('sv.tab_all')}
                    </button>
                    <button
                        onClick={() => setTab("active")}
                        className={`text-[14px] px-3 py-1 rounded ${tab === "active" ? "bg-green-600 text-white" : "bg-gray-100"}`}
                    >
                        {t('sv.tab_active')} ({countActive})
                    </button>
                    <button
                        onClick={() => setTab("locked")}
                        className={`text-[14px] px-3 py-1 rounded ${tab === "locked" ? "bg-red-500 text-white" : "bg-gray-100"}`}
                    >
                        {t('sv.tab_locked')} ({countLocked})
                    </button>
                </div>

                {/* SEARCH + ADD */}
                <div className="flex gap-2 flex-col md:flex-row w-full md:w-auto">
                    <Select
                        style={{ width: 200 }}
                        value={areaFilter}
                        onChange={setAreaFilter}
                        options={[
                            { value: '', label: t('sv.area_filter_all') },
                            { value: CHECKIN_AREA, label: t('sv.area_filter_checkin') },
                            { value: 'none', label: t('sv.area_filter_none') },
                        ]}
                    />
                    <Input
                        prefix={<SearchOutlined />}
                        placeholder={t('sv.search')}
                        style={{ width: 250 }}
                        value={keyword}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                    {canCreate && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openAdd}
                            className="max-w-[250px]"
                        >
                            {t('sv.add')}
                        </Button>
                    )}
                </div>
            </div>

            <div className="flex justify-between items-center mt-4 mb-2 gap-3 flex-wrap">
                {selectedIds.length ? (
                    // Chọn nhiều dịch vụ (kể cả khác trang) rồi gán / gỡ khỏi màn Check-in một lần
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-700">{t('sv.selected', { count: selectedIds.length })}</span>
                        <Button size="small" type="primary" loading={bulkAction === 'add'} disabled={!!bulkAction}
                            onClick={() => handleBulkDisplayArea('add')}>
                            {t('sv.area_add')}
                        </Button>
                        <Popconfirm title={t('sv.area_remove_confirm', { count: selectedIds.length })} onConfirm={() => handleBulkDisplayArea('remove')}>
                            <Button size="small" danger loading={bulkAction === 'remove'} disabled={!!bulkAction}>
                                {t('sv.area_remove')}
                            </Button>
                        </Popconfirm>
                        <Button size="small" type="link" disabled={!!bulkAction} onClick={() => setSelectedIds([])}>
                            {t('sv.clear_selection')}
                        </Button>
                    </div>
                ) : (
                    <span className="text-sm text-gray-500">
                        {t('sv.total')} {pagination.total} {t('sv.total_suffix')}
                    </span>
                )}
                <ColumnSettingButton {...columnSettingProps} />
            </div>

            <Spin spinning={loading}>
                <Table
                    columns={tableColumns}
                    components={tableComponents}
                    tableLayout={tableLayout}
                    dataSource={data}
                    rowKey="id"
                    rowSelection={canEdit ? {
                        selectedRowKeys: selectedIds,
                        onChange: setSelectedIds,
                        preserveSelectedRowKeys: true,
                    } : undefined}
                    scroll={{ x: scrollX }}
                    className="custom-ant-table"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        position: ["bottomRight"],
                    }}
                    onChange={handleTableChange}
                />
            </Spin>

            {/* MODAL */}
            <Modal
                title={editing ? t('sv.modal_edit') : t('sv.modal_add')}
                open={open}
                onCancel={() => setOpen(false)}
                onOk={handleSubmit}
                okText={t('sv.save')}
                confirmLoading={submitting}
                width={700}
                className="dark:[&_.ant-modal-content]:bg-[#1f1f1f] 
                dark:[&_.ant-modal-header]:bg-[#1f1f1f]
                dark:[&_.ant-modal-title]:text-white"
            >
                <Form
                    layout="vertical"
                    form={form}
                    className="grid grid-cols-1 md:grid-cols-2 gap-x-4"
                >
                    {/* TÊN */}
                    <Form.Item
                        label={t('sv.label_name')}
                        name="name"
                        rules={[{ required: true, message: t('sv.name_required') }]}
                        className="md:col-span-2"
                    >
                        <Input placeholder={t('sv.placeholder_name')} />
                    </Form.Item>

                    {/* MÃ DV */}
                    <Form.Item
                        label={t('sv.label_code')}
                        name="code"
                        rules={[{ required: true, message: t('sv.code_required') }]}
                    >
                        <Input placeholder="DV-00001" />
                    </Form.Item>

                    {/* MÃ DV PHỤ */}
                    <Form.Item
                        label={t('sv.label_code_2')}
                        name="code_2"
                        rules={[{ required: true, message: t('sv.code2_required') }]}
                    >
                        <Input />
                    </Form.Item>

                    {/* DANH MỤC */}
                    <Form.Item label={t('sv.label_category')} name="category_id">
                        <Select
                            placeholder={t('sv.placeholder_category')}
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                                removeDiacritics((option?.label as string || '').toLowerCase())
                                    .includes(removeDiacritics(input.toLowerCase()))
                            }
                            options={categories.map(c => ({ value: c.id, label: c.name }))}
                        />
                    </Form.Item>

                    {/* THỜI LƯỢNG */}
                    <Form.Item label={t('sv.label_duration')} name="duration">
                        <InputNumber className="w-full" min={0} addonAfter={t('sv.minutes')} />
                    </Form.Item>

                    {/* GIÁ */}
                    <Form.Item
                        label={t('sv.label_price')}
                        name="price"
                        rules={[{ required: true, message: t('sv.price_required') }]}
                    >
                        <InputNumber
                            className="w-full"
                            min={0}
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => value?.replace(/,/g, '') as any}
                        />
                    </Form.Item>

                    {/* GIÁ ƯU ĐÃI */}
                    <Form.Item label={t('sv.label_discount_price')} name="discount_price">
                        <InputNumber
                            className="w-full"
                            min={0}
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => value?.replace(/,/g, '') as any}
                        />
                    </Form.Item>

                    {/* ƯU TIÊN */}
                    <Form.Item label={t('sv.label_priority')} name="priority">
                        <InputNumber className="w-full" min={0} />
                    </Form.Item>

                    {/* MÔ TẢ */}
                    <Form.Item
                        label={t('sv.label_description')}
                        name="description"
                        className="md:col-span-2"
                    >
                        <Input.TextArea rows={3} placeholder={t('sv.placeholder_description')} />
                    </Form.Item>

                    {/* TRẠNG THÁI */}
                    <Form.Item
                        label={t('sv.label_status')}
                        name="status"
                        valuePropName="checked"
                        className="md:col-span-2"
                    >
                        <Switch
                            checkedChildren={t('sv.status_active')}
                            unCheckedChildren={t('sv.status_locked')}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
}
